/**
 * SQLite schema for the ontos derived index.
 *
 * The markdown frontmatter `ontos:` block is the source of truth; this index is
 * a regenerable derivative (delete the .sqlite file and re-run `rebuild-ontos`).
 * Multi-hop traversal is a recursive CTE over `relations` — no graph DB
 * (TypeDB/Neo4j) is needed at Phase 1 scale (see the build spec, storage section).
 */
export const SCHEMA = `
CREATE TABLE IF NOT EXISTS objects (
  id TEXT PRIMARY KEY,
  type TEXT NOT NULL,
  subtype TEXT,
  label TEXT NOT NULL,
  source TEXT
);
CREATE INDEX IF NOT EXISTS idx_objects_type ON objects(type);

CREATE TABLE IF NOT EXISTS relations (
  rel_id TEXT PRIMARY KEY,
  rel TEXT NOT NULL,
  source_id TEXT NOT NULL,
  target_id TEXT NOT NULL,
  provenance TEXT NOT NULL,
  confidence REAL NOT NULL DEFAULT 1.0,
  confirmed TEXT,
  evidence TEXT,
  FOREIGN KEY (source_id) REFERENCES objects(id) ON DELETE CASCADE,
  FOREIGN KEY (target_id) REFERENCES objects(id) ON DELETE CASCADE
);
CREATE INDEX IF NOT EXISTS idx_relations_source ON relations(source_id, rel);
CREATE INDEX IF NOT EXISTS idx_relations_target ON relations(target_id, rel);

CREATE TABLE IF NOT EXISTS aliases (
  alias TEXT PRIMARY KEY,
  canonical_id TEXT NOT NULL,
  FOREIGN KEY (canonical_id) REFERENCES objects(id) ON DELETE CASCADE
);

-- LLM enrichment proposals (0.2.0). DELIBERATELY SEPARATE from \`relations\`:
-- the trusted graph (\`relations\`) is written only by deterministic extraction
-- or by human-confirmed promotion, so \`why\`/the recurring-failure guard — which
-- read \`relations\` with no trust filter — are never silently influenced by an
-- LLM guess. No FK to \`objects\`: a proposed source/target may be UNRESOLVED (a
-- raw label the LLM produced that is not yet a canonical object). Survives the
-- deterministic rebuild's \`clear()\` (LLM proposals are expensive to regenerate).
CREATE TABLE IF NOT EXISTS relation_proposals (
  rel_id TEXT PRIMARY KEY,
  rel TEXT NOT NULL,
  source_id TEXT NOT NULL,
  target_id TEXT NOT NULL,
  source_resolved INTEGER NOT NULL DEFAULT 0,
  target_resolved INTEGER NOT NULL DEFAULT 0,
  trust TEXT NOT NULL,               -- high | medium | low
  trust_rank INTEGER NOT NULL,       -- 3 | 2 | 1 (monotonic upgrade + ordering)
  verified_by TEXT NOT NULL,         -- deterministic | cross-model | self
  confidence REAL NOT NULL DEFAULT 0,
  evidence_path TEXT,
  evidence_span TEXT,
  source_record_hash TEXT,           -- the extraction hash that produced this
  proposed_at TEXT,
  confirmed TEXT,                    -- ISO date if promoted into \`relations\`; null = proposed
  rejected_at TEXT,                  -- ISO date a human rejected it (sticky: no re-surface/re-ask)
  asked_at TEXT,                     -- ISO date last surfaced as an in-context question (cooldown)
  -- Phase-2 (conversation extraction): provenance widened beyond curated records.
  source_kind TEXT NOT NULL DEFAULT 'record',  -- record | conversation
  source_family TEXT,                -- originating transcript/record id; blocks a conversation
                                     -- triple from self-corroborating a record DERIVED from it
  extraction_run_id TEXT,            -- batch id, so a bad extractor run can be rolled back wholesale
  -- Trust/usage policy (2026-06-20 pivot). Three SEPARATE axes so a single
  -- \`>= HIGH\` can't punch the safety line. evidence_kind = how trust was earned;
  -- conversation_use = dialogue hedge (derived); guard_eligible = may a RISK guard
  -- read it (derived; true only for deterministic/explicit). score accumulates,
  -- acquiescence nudges it, a correction slams it negative + sets contested.
  evidence_kind TEXT,                       -- deterministic|explicit|cross_model|self|acquiescence
  conversation_use TEXT,                    -- low|medium|high (derived from score+caps)
  guard_eligible INTEGER NOT NULL DEFAULT 0,
  -- Sticky taint: 1 once ANY conversation-sourced write touched this row. A tainted
  -- row can never be AUTO guard-eligible, even if a later record-sourced merge leaves
  -- source_kind='record' while evidence_kind was upgraded by the conversation writer
  -- (the provenance/evidence decoupling leak — Codex review). Fail closed on the guard.
  conversation_touched INTEGER NOT NULL DEFAULT 0,
  contested INTEGER NOT NULL DEFAULT 0,
  score REAL NOT NULL DEFAULT 0,
  acquiescence_count INTEGER NOT NULL DEFAULT 0,
  last_acquiesced_at TEXT,
  fact_type TEXT,                           -- objective|preference|habit|project_context|state|plan|relationship|environment
  derived_from TEXT                         -- parent rel_id (derived <= parent clamp)
);
CREATE INDEX IF NOT EXISTS idx_proposals_trust ON relation_proposals(trust_rank);

-- Phase-2 evidence, append-only, ONE row per (proposal, transcript, message, quote).
-- Kept SEPARATE from relation_proposals (which holds only aggregate state) so that
-- cross-conversation corroboration is a COUNT(DISTINCT transcript_id) — a derived
-- value, never a stored counter that re-runs/overlapping chunks could double-count
-- (Codex review 2026-06-19 #2). The PK makes re-ingesting the same evidence a no-op
-- (idempotent). quote_hash anchors the claim to verifiable source text (#8).
CREATE TABLE IF NOT EXISTS proposal_evidence (
  rel_id TEXT NOT NULL,
  transcript_id TEXT NOT NULL,
  message_id TEXT NOT NULL,
  span_start INTEGER,
  span_end INTEGER,
  quote_hash TEXT NOT NULL,
  speaker TEXT,                      -- user | agent | third-party (speaker authority, §7)
  run_id TEXT,
  message_at TEXT,                   -- when the cited MESSAGE was said (date anchor for the fact;
                                     -- taken from the parsed transcript at ingest, never from the LLM)
  PRIMARY KEY (rel_id, transcript_id, message_id, quote_hash)
);
CREATE INDEX IF NOT EXISTS idx_pevidence_rel ON proposal_evidence(rel_id);

-- Phase-2 (MVP-B) object proposals. Conversations surface NEW entities not yet in
-- the trusted objects table (a component, a person) — propose them here so a relation
-- has something to attach to. SEPARATE from objects: an unconfirmed object never
-- affects traversal/guard. Confirm promotes it into the objects table with its
-- proposed id; merge-to-existing is a later refinement (Codex review 2026-06-19 #4/#11).
CREATE TABLE IF NOT EXISTS object_proposals (
  obj_id TEXT PRIMARY KEY,           -- proposed canonical id (type:slug)
  type TEXT NOT NULL,                -- one of OBJECT_TYPES
  label TEXT NOT NULL,
  trust TEXT NOT NULL,
  trust_rank INTEGER NOT NULL,
  verified_by TEXT NOT NULL,
  confidence REAL NOT NULL DEFAULT 0,
  source_kind TEXT NOT NULL DEFAULT 'conversation',
  source_family TEXT,
  evidence_path TEXT,
  evidence_span TEXT,
  extraction_run_id TEXT,
  proposed_at TEXT,
  confirmed TEXT,                    -- ISO date promoted into the objects table; null = proposed
  rejected_at TEXT,
  asked_at TEXT,
  -- Trust/usage policy (2026-06-20 pivot) — same axes as relation_proposals.
  evidence_kind TEXT,
  conversation_use TEXT,
  guard_eligible INTEGER NOT NULL DEFAULT 0,
  conversation_touched INTEGER NOT NULL DEFAULT 0,  -- sticky guard taint (see relation_proposals)
  contested INTEGER NOT NULL DEFAULT 0,
  score REAL NOT NULL DEFAULT 0,
  acquiescence_count INTEGER NOT NULL DEFAULT 0,
  last_acquiesced_at TEXT,
  fact_type TEXT,
  derived_from TEXT
);
CREATE INDEX IF NOT EXISTS idx_obj_proposals_trust ON object_proposals(trust_rank);

-- Phase-2 chunk-level processing state. NOT transcript-level: a transcript whose
-- chunks yield zero candidates must still be marked done (else it is reprocessed
-- forever), and a partially-failed transcript must not mark its unprocessed chunks
-- done (Codex review 2026-06-19 #9). Versions let a fixed extractor reprocess (#16).
CREATE TABLE IF NOT EXISTS conversation_chunk_state (
  transcript_id TEXT NOT NULL,
  chunk_id TEXT NOT NULL,
  status TEXT NOT NULL,              -- processed_no_candidates | processed_with_candidates | failed_retryable | failed_permanent
  extractor_version TEXT,
  prompt_version TEXT,
  schema_version TEXT,
  processed_at TEXT,
  PRIMARY KEY (transcript_id, chunk_id)
);

-- Per-record extraction state for change detection (§4 step 3 "changed files
-- only"). EXTRACTION hash (did the text change?) is tracked separately from
-- verification state, so a text-unchanged record can still get a verification-only
-- re-run that upgrades a proposal's trust when a cross-checker becomes available.
CREATE TABLE IF NOT EXISTS enrichment_state (
  record_path TEXT PRIMARY KEY,
  extraction_hash TEXT NOT NULL,
  enriched_at TEXT
);

-- Transcript-level extraction watermark (auto-extraction at session start).
-- conversation_chunk_state above is per-chunk and keyed by a POSITIONAL chunk id,
-- so changing the chunk params re-chunks the same transcript into different ids and
-- silently re-extracts it. This table answers "is this whole transcript already
-- extracted?" by the file's CONTENT HASH, which survives re-chunking and detects an
-- edited transcript (hash differs -> re-extract). A row is written only once every
-- chunk of the transcript has a processed chunk-state, so a partially-extracted
-- transcript is NOT marked done. count 0 also records a reviewed-but-empty
-- transcript, closing the "empty transcript re-walked every scan" gap. MACHINE-LOCAL
-- like the rest of this index (the proposals it gates live in this same per-machine
-- sqlite, so the watermark must be per-machine too — a synced watermark would tell
-- another machine "done" when it holds none of the resulting proposals).
CREATE TABLE IF NOT EXISTS conversation_extraction_state (
  transcript_id TEXT PRIMARY KEY,
  content_hash TEXT NOT NULL,        -- sha256 of the normalized .jsonl bytes
  proposal_count INTEGER NOT NULL DEFAULT 0,  -- chunks that yielded candidates (informational)
  extractor_version TEXT,
  extracted_at TEXT,
  chunk_sig TEXT                     -- "maxChars/overlap" the in-progress chunks were cut with;
                                     -- POSITIONAL chunk ids are only comparable under the SAME
                                     -- params, so completion may be decided outside --scan only
                                     -- when the deciding invocation's sig matches (Codex 2026-07-13)
);

-- Last extraction scan/emit attempt (single row) — makes the opt-in gate
-- VISIBLE: with mining off the scan used to leave NO trace at all, so "not
-- extracted" was indistinguishable from "never ran / broke". A curated-mode
-- skip is now recorded explicitly ('curated-skip'), a real pass as 'mined'
-- (2026-06-29 review condition). Surfaced in the session-start 🧠 line and
-- readable by any dashboard from this same machine-local index.
CREATE TABLE IF NOT EXISTS extraction_scan_status (
  id INTEGER PRIMARY KEY CHECK (id = 1),
  mode TEXT NOT NULL,   -- 'curated-skip' | 'mined'
  op TEXT NOT NULL,     -- 'scan' | 'emit'
  at TEXT NOT NULL,     -- ISO timestamp (caller-supplied)
  detail TEXT           -- optional JSON (e.g. emitted/deferred counts)
);
`;
